import React, { useEffect, useState } from 'react';
import { Button } from '../../components';
import {
  getIncomingRequests,
  acceptRequest,
  rejectRequest,
  MentoringRequestDoc,
} from '../../services/mentoring-request-service';

function fmtSlot(scheduledAt: string, startTime: string, endTime: string): string {
  const d = new Date(scheduledAt);
  const date = d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  return `${date}, ${startTime} – ${endTime}`;
}

const statusBadge: Record<string, string> = {
  pending:   'bg-warning/15 text-warning',
  accepted:  'bg-success/15 text-success',
  rejected:  'bg-danger/15 text-danger',
  expired:   'bg-muted/15 text-muted',
  scheduled: 'bg-info/15 text-info',
};

const MentorshipRequests: React.FC = () => {
  const [requests, setRequests] = useState<MentoringRequestDoc[]>([]);
  const [loading, setLoading]   = useState(true);
  const [acting, setActing]     = useState<string | null>(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    getIncomingRequests()
      .then(setRequests)
      .catch(() => setError('Failed to load requests.'))
      .finally(() => setLoading(false));
  }, []);

  const handle = async (id: string, action: 'accept' | 'reject') => {
    setActing(id);
    setError('');
    try {
      const updated = action === 'accept' ? await acceptRequest(id) : await rejectRequest(id);
      setRequests(prev => prev.map(r => r._id === id ? updated : r));
    } catch (e: any) {
      setError(e?.message || 'Action failed. Try again.');
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <div className="w-4 h-4 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        <span className="text-muted text-sm">Loading requests…</span>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-text font-semibold text-lg mb-4">Mentorship Requests</h3>
      {error && <p className="text-danger text-sm mb-4">{error}</p>}

      {requests.length === 0 ? (
        <p className="text-muted text-sm">No incoming requests yet.</p>
      ) : (
        <div className="space-y-3">
          {requests.map(r => {
            const founder = r.founderId as any;
            const isPending = r.status === 'pending';
            return (
              <div key={r._id} className="border border-line rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-text font-medium text-sm">{founder?.name || 'Founder'}</p>
                    {founder?.founder?.startupName && (
                      <p className="text-muted text-xs">{founder.founder.startupName}</p>
                    )}
                    <p className="text-muted text-xs mt-1">{fmtSlot(r.scheduledAt, r.startTime, r.endTime)}</p>
                    {r.sessionId && (
                      <p className="text-chart text-xs mt-1">Session created</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge[r.status] || 'bg-surface text-muted'}`}>
                      {r.status}
                    </span>
                    {isPending && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={acting === r._id}
                          onClick={() => handle(r._id, 'accept')}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={acting === r._id}
                          onClick={() => handle(r._id, 'reject')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MentorshipRequests;
